import {IStyleEditorLocalization, StyleEditor as MaeStyleEditor} from '@mae/styleeditor';
import * as React from "react";
import {defineMessages, FormattedMessage, InjectedIntlProps, injectIntl} from "react-intl";
import * as riaApi from '../common/ui/riamap/RiaApi';
import {loadCssfile as loadCssFile, removeMainStyles} from '../common/util/Util';
import {WithApi, WithApiProperties} from '../common/util/WithApi';
import {StyleEditorCommand, StyleEditorInitMessage, StyleEditorMessage} from './model';
import {WFSCapabilities} from "@luciad/ria/model/capabilities/WFSCapabilities.js";
import {WFSFeatureStore, WFSQueryOptions} from "@luciad/ria/model/store/WFSFeatureStore.js";
import {FeatureModel} from '@luciad/ria/model/feature/FeatureModel.js';
import {FeatureLayer} from '@luciad/ria/view/feature/FeatureLayer.js';
import {GMLCodec} from "@luciad/ria/model/codec/GMLCodec.js";
import {FeatureTypePropertyType} from '../services/model';
import {WebGLMap} from "@luciad/ria/view/WebGLMap.js";
import {ApolloApi} from "../api/ApolloApi";
import * as epsgIndexAll from "epsg-index/all.json";
import {LoadSpatially} from "@luciad/ria/view/feature/loadingstrategy/LoadSpatially.js";
import {QueryProvider} from "@luciad/ria/view/feature/QueryProvider.js";
import {Alert} from "react-bootstrap/lib";
import {
  addReference,
  getReference,
  isValidReferenceIdentifier,
  parseWellKnownText
} from "@luciad/ria/reference/ReferenceProvider.js";
import {getLanguageAndLocale} from "../common/intl/intl";

type StyleEditorProps = InjectedIntlProps & WithApiProperties;

const allEpsg = epsgIndexAll;
const symbolList: Record<string, string> = {};

const STYLE_EDITOR_MESSAGES = defineMessages({
  NO_FEATURES: {
    id: "studio.style.vector-editor.no-features",
    defaultMessage: "No supported features found on this WFS service.",
  },
  NO_PREVIEW: {
    id: "studio.style.vector-editor.no-preview",
    defaultMessage: "Coordinate system not supported, no preview is available for selected WFS service.",
  },
  ADD_SYMBOLIZER: {
    id: "mae.styleeditor.button.add_symbolizer",
    defaultMessage: "Add symbolizer"
  },
  ADD_RULE: {
    id: "mae.styleeditor.button.add_rule",
    defaultMessage: "Add rule"
  },
  BIND_DB: {
    id: "mae.styleeditor.button.bind_db",
    defaultMessage: "Bind to database column"
  },
  DELETE_RULE: {
    id: "mae.styleeditor.button.delete_rule",
    defaultMessage: "Delete rule"
  },
  DELETE_SYMBOLIZER: {
    id: "mae.styleeditor.button.delete_symbolizer",
    defaultMessage: "Delete symbolizer"
  },
  DELETE_STYLE: {
    id: "mae.styleeditor.button.delete_style",
    defaultMessage: "Delete style"
  },
  PAN_TO_LAYER: {
    id: "mae.styleeditor.button.pan_to_layer",
    defaultMessage: "Pan to layer"
  },
  HIDE_LAYER: {
    id: "mae.styleeditor.button.hide_layer",
    defaultMessage: "Hide layer"
  },
  MAKE_LAYER_PERMANENT: {
    id: "mae.styleeditor.button.make_layer_permanent",
    defaultMessage: "Make layer permanent"
  },
  NO_BOUNDS: {
    id: "mae.styleeditor.button.no_bounds",
    defaultMessage: "No bounds information available"
  },
  POINT_PLACEMENT: {
    id: "mae.styleeditor.button.point_placement",
    defaultMessage: "Point placement"
  },
  EXPERT_POINT_PLACEMENT: {
    id: "mae.styleeditor.button.expert_point_placement",
    defaultMessage: "Expert point placement"
  },
  LINE_PLACEMENT: {
    id: "mae.styleeditor.button.line_placement",
    defaultMessage: "Line placement"
  },
  SAVE: {
    id: "mae.styleeditor.button.save",
    defaultMessage: "Save"
  },
  NO_STYLE_SELECTED: {
    id: "mae.styleeditor.hints.no_style_selected",
    defaultMessage: "No style selected"
  },
  REFRESH_NECESSARY: {
    id: "mae.styleeditor.hints.refresh_necessary",
    defaultMessage: "Saving of the styleset is necessary to get database value for this field"
  },
  INVALID_EXTERNAL_REFERENCE: {
    id: "mae.styleeditor.hints.invalid_external_reference",
    defaultMessage: "Invalid external reference"
  },
  CONTAINS_INVALID_EXTERNAL_REFERENCE: {
    id: "mae.styleeditor.hints.contains_invalid_external_reference",
    defaultMessage: "Contains invalid external reference"
  },
  CHANGES_NOT_SAVED: {
    id: "mae.styleeditor.hints.changes_not_saved", defaultMessage: "You'll lose your data!"
  },
  SCALE_TOO_BIG: {
    id: "mae.styleeditor.hints.scale_too_big",
    defaultMessage: "The current scale is too big. Zoom in to see the layer."
  },
  SCALE_TOO_SMALL: {
    id: "mae.styleeditor.hints.scale_too_small",
    defaultMessage: "The current scale is too small. Zoom out to see the layer."
  },
  COLOR_SCHEME: {
    id: "mae.styleeditor.wizards.color_scheme",
    defaultMessage: "Color scheme"
  },
  CATEGORIES: {
    id: "mae.styleeditor.wizards.categories",
    defaultMessage: "Categories"
  },
  DISTRIBUTION: {
    id: "mae.styleeditor.wizards.distribution",
    defaultMessage: "Distribution"
  },
  CLASSES: {
    id: "mae.styleeditor.wizards.classes",
    defaultMessage: "Classes"
  },
  FIELD: {
    id: "mae.styleeditor.wizards.field",
    defaultMessage: "Field"
  },
  STYLE: {
    id: "mae.styleeditor.wizards.style",
    defaultMessage: "Style"
  },
  CATEGORIZE: {
    id: "mae.styleeditor.wizards.title.categorize",
    defaultMessage: "Generate categorized rules"
  },
  GRATUATED: {
    id: "mae.styleeditor.wizards.title.Gratuated",
    defaultMessage: "Generate gratuated rules"
  },
  MOST_RELEVANT_HINT: {
    id: "mae.styleeditor.wizards.hints.most_relevant_hint",
    defaultMessage: "Most relevant values selected automatically due to selected db field containing more than 10 distinct values."
  },
  LESS_THAN_THREE_HINT: {
    id: "mae.styleeditor.wizards.hints.less_than_three_hint",
    defaultMessage: "Selected database field contains less than 3 distinct values. Categorization not supported."
  },
  NO_DB_FIELDS_AVAILABLE: {
    id: "mae.styleeditor.wizards.hints.no_db_fields_available",
    defaultMessage: "No db fields available for selected style"
  },
  CLASSIFY_ONLY_FOR_NUMERIC_VALUES: {
    id: "mae.styleeditor.wizards.hints.classify_only_for_numeric_values",
    defaultMessage: "Caution: Generating graduated rules is only possible for numeric values"
  },
  MORE_THAN_50_CATEGORIES: {
    id: "mae.styleeditor.wizards.hints.more_than_50_categories",
    defaultMessage: "Caution: Color generation is only available for up to 54 categories. More colors have to be selected manually"
  },
  DB_FIELDS_COULD_NOT_BE_FETCHED: {
    id: "mae.styleeditor.wizards.errors.db_fields_could_not_be_fetched",
    defaultMessage: "Error: Database fields could not be fetched."
  },
  VALUES_OF_DB_FIELDS_COULD_NOT_BE_FETCHED: {
    id: "mae.styleeditor.wizards.errors.values_of_db_fields_could_not_be_fetched",
    defaultMessage: "Error: Values of field could not be fetched."
  },
  NO_FILE_SELECTED: {
    id: "mae.styleeditor.file_upload_component.no_file_selected",
    defaultMessage: "-- No file selected --"
  },
  INVALID_REFERENCE_PLEASE_CHECK: {
    id: "mae.styleeditor.file_upload_component.invalid_reference_please_check",
    defaultMessage: "-- Invalid reference, please check --"
  },
  NO_FILES_AVAILABLE: {
    id: "mae.styleeditor.file_upload_component.no_files_available",
    defaultMessage: "-- No files available. Please upload --"
  },
  TEXT: {
    id: "mae.styleeditor.general.text",
    defaultMessage: "Text"
  },
  SIMPLE: {
    id: "mae.styleeditor.general.simple",
    defaultMessage: "Simple"
  },
  EXPERT: {
    id: "mae.styleeditor.general.expert",
    defaultMessage: "Expert"
  },
  SIMPLE_FILL: {
    id: "mae.styleeditor.general.simple_fill",
    defaultMessage: "Simple fill"
  },
  GRAPHIC_FILL_FILLED_MARK: {
    id: "mae.styleeditor.general.graphic_fill_filled_mark",
    defaultMessage: "Graphic fill | Filled mark"
  },
  GRAPHIC_FILL_STROKED_MARK: {
    id: "mae.styleeditor.general.graphic_fill_stroked_mark",
    defaultMessage: "Graphic fill | Stroked mark"
  },
  GRAPHIC_FILL_STROKED_AND_FILLED_MARK: {
    id: "mae.styleeditor.general.graphic_fill_stroked_and_filled_mark",
    defaultMessage: "Graphic fill | Filled & Stroked mark"
  },
  GRAPHIC_FILL_EXTERNAL_GRAPHIC: {
    id: "mae.styleeditor.general.graphic_fill_external_graphic",
    defaultMessage: "Graphic fill | External graphic"
  },
  CUSTOM_FILL: {
    id: "mae.styleeditor.general.custom_fill",
    defaultMessage: "Custom Fill"
  },
  STROKED_MARK: {
    id: "mae.styleeditor.general.stroked_mark",
    defaultMessage: "Stroked mark"
  },
  FILLED_MARK: {
    id: "mae.styleeditor.general.filled_mark",
    defaultMessage: "Filled mark"
  },
  FILLED_AND_STROKED_MARK: {
    id: "mae.styleeditor.general.filled_and_stroked_mark",
    defaultMessage: "Filled & Stroked mark"
  },
  EXTERNAL_GRAPHIC: {
    id: "mae.styleeditor.general.external_graphic",
    defaultMessage: "External graphic"
  },
  CUSTOM_HALO: {
    id: "mae.styleeditor.general.custom_halo",
    defaultMessage: "Custom Halo"
  },
  CUSTOM_GRAPHIC: {
    id: "mae.styleeditor.general.custom_graphic",
    defaultMessage: "Custom graphic"
  },
  SIMPLE_LINE: {
    id: "mae.styleeditor.general.simple_line",
    defaultMessage: "Simple line"
  },
  DASHED_LINE: {
    id: "mae.styleeditor.general.dashed_line",
    defaultMessage: "Dashed line"
  },
  GRAPHIC_STROKE_STROKED_MARK: {
    id: "mae.styleeditor.general.graphic_stroke_stroked_mark",
    defaultMessage: "Graphic stroke | Stroked mark"
  },
  GRAPHIC_STROKE_FILLED_MARK: {
    id: "mae.styleeditor.general.graphic_stroke_filled_mark",
    defaultMessage: "Graphic stroke | Filled mark"
  },
  GRAPHIC_STROKE_FILLED_AND_STROKED_MARK: {
    id: "mae.styleeditor.general.graphic_stroke_filled_and_stroked_mark",
    defaultMessage: "Graphic stroke | Filled & Stroked mark"
  },
  GRAPHIC_STROKE_EXTERNAL_GRAPHIC: {
    id: "mae.styleeditor.general.graphic_stroke_External_graphic",
    defaultMessage: "Graphic stroke | External graphic"
  },
  LEFT: {
    id: "mae.styleeditor.general.left",
    defaultMessage: "Left"
  },
  BOTTOM: {
    id: "mae.styleeditor.general.bottom",
    defaultMessage: "Bottom"
  },
  RIGHT: {
    id: "mae.styleeditor.general.right",
    defaultMessage: "Right"
  },
  TOP: {
    id: "mae.styleeditor.general.top",
    defaultMessage: "Top"
  },
  CENTER: {
    id: "mae.styleeditor.general.center",
    defaultMessage: "Center"
  },
  SQUARE: {
    id: "mae.styleeditor.general.square",
    defaultMessage: "Square"
  },
  CIRCLE: {
    id: "mae.styleeditor.general.circle",
    defaultMessage: "Circle"
  },
  TRIANGLE: {
    id: "mae.styleeditor.general.triangle",
    defaultMessage: "Triangle"
  },
  CROSS: {
    id: "mae.styleeditor.general.cross",
    defaultMessage: "Cross"
  },
  STAR: {
    id: "mae.styleeditor.general.star",
    defaultMessage: "Star"
  },
  UNIT: {
    id: "mae.styleeditor.general.unit",
    defaultMessage: "Unit"
  },
  FILL: {
    id: "mae.styleeditor.general.fill",
    defaultMessage: "Fill"
  },
  FILL_COLOR: {
    id: "mae.styleeditor.general.fill_color",
    defaultMessage: "Fill color"
  },
  FILL_OPACITY: {
    id: "mae.styleeditor.general.fill_opacity",
    defaultMessage: "Fill opacity"
  },
  STROKE: {
    id: "mae.styleeditor.general.stroke",
    defaultMessage: "Stroke"
  },
  STROKE_WIDTH: {
    id: "mae.styleeditor.general.stroke_width",
    defaultMessage: "Stroke width"
  },
  STROKE_COLOR: {
    id: "mae.styleeditor.general.stroke_color",
    defaultMessage: "Stroke color"
  },
  STROKE_OPACITY: {
    id: "mae.styleeditor.general.stroke_opacity",
    defaultMessage: "Stroke opacity"
  },
  NAME: {
    id: "mae.styleeditor.general.name",
    defaultMessage: "Name"
  },
  LABEL_TEXT: {
    id: "mae.styleeditor.general.label_text",
    defaultMessage: "Label text"
  },
  HORIZONTAL: {
    id: "mae.styleeditor.general.horizontal",
    defaultMessage: "Horizontal"
  },
  VERTICAL: {
    id: "mae.styleeditor.general.vertical",
    defaultMessage: "Vertical"
  },
  LABEL_PLACEMENT: {
    id: "mae.styleeditor.general.label_placement",
    defaultMessage: "Label placement"
  },
  GRAPHIC_SIZE: {
    id: "mae.styleeditor.general.graphic_size",
    defaultMessage: "Graphic size"
  },
  GRAPHIC_FILL: {
    id: "mae.styleeditor.general.graphic_fill",
    defaultMessage: "Graphic fill"
  },
  GRAPHIC_IMAGE_SIZE: {
    id: "mae.styleeditor.general.graphic_image_size",
    defaultMessage: "Use image size"
  },
  FONT_FAMILY: {
    id: "mae.styleeditor.general.font_family",
    defaultMessage: "Font family"
  },
  FONT_SIZE: {
    id: "mae.styleeditor.general.font_size",
    defaultMessage: "Font size"
  },
  FONT_WEIGHT: {
    id: "mae.styleeditor.general.font_weight",
    defaultMessage: "Font weight"
  },
  FONT_STYLE: {
    id: "mae.styleeditor.general.font_style",
    defaultMessage: "Font style"
  },
  OPACITY: {
    id: "mae.styleeditor.general.opacity",
    defaultMessage: "Opacity"
  },
  ROTATION: {
    id: "mae.styleeditor.general.rotation",
    defaultMessage: "Rotation"
  },
  DISPLACEMENT_X: {
    id: "mae.styleeditor.general.displacement_X",
    defaultMessage: "Displacement X"
  },
  DISPLACEMENT_Y: {
    id: "mae.styleeditor.general.displacement_Y",
    defaultMessage: "Displacement Y"
  },
  MARKTYPE: {
    id: "mae.styleeditor.general.marktype",
    defaultMessage: "Marktype"
  },
  DASHARRAY: {
    id: "mae.styleeditor.general.dasharray",
    defaultMessage: "Dasharray"
  },
  DASHOFFSET: {
    id: "mae.styleeditor.general.dashoffset",
    defaultMessage: "Dashoffset"
  },
  INITIAL_GAP: {
    id: "mae.styleeditor.general.initial_gap",
    defaultMessage: "Initial gap"
  },
  GAP: {
    id: "mae.styleeditor.general.gap",
    defaultMessage: "Gap"
  },
  HALO: {
    id: "mae.styleeditor.general.halo",
    defaultMessage: "Halo"
  },
  RADIUS: {
    id: "mae.styleeditor.general.radius",
    defaultMessage: "Radius"
  },
  ANCHOR_POINT: {
    id: "mae.styleeditor.general.anchor_point",
    defaultMessage: "AnchorPoint"
  },
  IS_ALIGNED: {
    id: "mae.styleeditor.general.is_aligned",
    defaultMessage: "Is aligned"
  },
  IS_REPEATED: {
    id: "mae.styleeditor.general.is_repeated",
    defaultMessage: "Is repeated"
  },
  PERPENDICULAR_OFFSET: {
    id: "mae.styleeditor.general.perpendicular_offset",
    defaultMessage: "Perpendicular offset"
  },
  GRAPHIC_REFERENCES: {
    id: "mae.styleeditor.general.graphic_references",
    defaultMessage: "Graphic references"
  },
  NONE: {
    id: "mae.styleeditor.general.none",
    defaultMessage: "None"
  },
  CUSTOM: {
    id: "mae.styleeditor.general.custom",
    defaultMessage: "Custom"
  },
  LINECAP: {
    id: "mae.styleeditor.general.linecap",
    defaultMessage: "Linecap"
  },
  GRAPHIC: {
    id: "mae.styleeditor.general.graphic",
    defaultMessage: "Graphic"
  },
  FILE: {
    id: "mae.styleeditor.general.file",
    defaultMessage: "File"
  },
  LINEJOIN: {
    id: "mae.styleeditor.general.linejoin",
    defaultMessage: "Linejoin"
  },
  DATABASE: {
    id: "mae.styleeditor.general.database",
    defaultMessage: "Use database value."
  },
  SELECT_A_FILE: {
    id: "mae.styleeditor.general.select_a_file",
    defaultMessage: "Select a file"
  },
  UPLOAD_A_FILE: {
    id: "mae.styleeditor.general.upload_a_file",
    defaultMessage: "Upload"
  },
  FILE_UPLOAD_ERROR: {
    id: "mae.styleeditor.general.file_upload_error",
    defaultMessage: "Could not upload file"
  },
  FILE_DELETE_ERROR: {
    id: "mae.styleeditor.general.file_delete_error",
    defaultMessage: "Could not delete file"
  },
  SELECT_FIELD: {
    id: "mae.styleeditor.general.select_field",
    defaultMessage: "Select field"
  },
  DELETE_CUSTOM_RULE: {
    id: "mae.styleeditor.general.delete_custom_Rule",
    defaultMessage: "Deletes all changes, rule will again be influenced by default rule"
  },
  UNLOCK_RULE: {
    id: "mae.styleeditor.general.unlock_Rule",
    defaultMessage: "Allows edit. <b>Warning </b>: Changes in default rule will no longer influence this rule!"
  },
  SHOW_BIG_PREVIEW: {
    id: "mae.styleeditor.general.show_big_preview",
    defaultMessage: "Show maximized preview."
  },
  HIDE_BIG_PREVIEW: {
    id: "mae.styleeditor.general.hide_big_preview",
    defaultMessage: "Hide maximized preview."
  },
  UNLOCK_SYMBOLOGY_TYPE: {
    id: "mae.styleeditor.general.unlock_symbology_type",
    defaultMessage: "Allows you to set symbology type manually."
  },
  LOCK_SYMBOLOGY_TYPE: {
    id: "mae.styleeditor.general.lock_symbology_type",
    defaultMessage: "Symbology type will be set automatically."
  },
  SYMBOLOGY_TYPE: {
    id: "mae.styleeditor.general.symbology_type",
    defaultMessage: "Symbology type"
  },
  SYMBOLIZER: {
    id: "mae.styleeditor.general.symbolizer",
    defaultMessage: "Symbolizer"
  },
  SYMBOL_LOAD_ERROR: {
    id: "mae.styleeditor.general.symbol_load_error",
    defaultMessage: "Error loading symbols"
  },
  FROM: {
    id: "mae.styleeditor.general.from",
    defaultMessage: "From"
  },
  TO: {
    id: "mae.styleeditor.general.to",
    defaultMessage: "To"
  },
  SCALE: {
    id: "mae.styleeditor.general.scale",
    defaultMessage: "Scale"
  },
  SEARCH: {
    id: "mae.styleeditor.general.search",
    defaultMessage: "Search"
  },
  FILTER: {
    id: "mae.styleeditor.general.filter",
    defaultMessage: "Filter"
  },
  RULE: {
    id: "mae.styleeditor.general.rule",
    defaultMessage: "Rule"
  },
  LOADING: {
    id: "mae.styleeditor.general.loading",
    defaultMessage: "Loading..."
  },
  UNSTYLED_LAYER: {
    id: "mae.styleeditor.general.unstyled_layer",
    defaultMessage: "Unstyled"
  },
  INITIALIZING_STYLEEDITOR: {
    id: "mae.styleeditor.general.initializing_styleeditor",
    defaultMessage: "Initializing editor, please wait..."
  },
  SOMETHING_WENT_WRONG: {
    id: "mae.styleeditor.general.something_went_wrong",
    defaultMessage: "Unexpected Error. \nWe're sorry, but unfortunately something went wrong while initializing the editor."
  }
});

const fontCheck = new Set([
  // Windows 10
  'Arial', 'Arial Black', 'Bahnschrift', 'Calibri', 'Cambria', 'Cambria Math', 'Candara', 'Comic Sans MS', 'Consolas',
  'Constantia', 'Corbel', 'Courier New', 'Ebrima', 'Franklin Gothic Medium', 'Gabriola', 'Gadugi', 'Georgia',
  'HoloLens MDL2 Assets', 'Impact', 'Ink Free', 'Javanese Text', 'Leelawadee UI', 'Lucida Console',
  'Lucida Sans Unicode', 'Malgun Gothic', 'Marlett', 'Microsoft Himalaya', 'Microsoft JhengHei',
  'Microsoft New Tai Lue',
  'Microsoft PhagsPa', 'Microsoft Sans Serif', 'Microsoft Tai Le', 'Microsoft YaHei', 'Microsoft Yi Baiti',
  'MingLiU-ExtB',
  'Mongolian Baiti', 'MS Gothic', 'MV Boli', 'Myanmar Text', 'Nirmala UI', 'Palatino Linotype', 'Segoe MDL2 Assets',
  'Segoe Print', 'Segoe Script', 'Segoe UI', 'Segoe UI Historic', 'Segoe UI Emoji', 'Segoe UI Symbol', 'SimSun',
  'Sitka',
  'Sylfaen', 'Symbol', 'Tahoma', 'Times New Roman', 'Trebuchet MS', 'Verdana', 'Webdings', 'Wingdings', 'Yu Gothic',
  // macOS
  'American Typewriter', 'Andale Mono', 'Arial', 'Arial Black', 'Arial Narrow', 'Arial Rounded MT Bold',
  'Arial Unicode MS',
  'Avenir', 'Avenir Next', 'Avenir Next Condensed', 'Baskerville', 'Big Caslon', 'Bodoni 72', 'Bodoni 72 Oldstyle',
  'Bodoni 72 Smallcaps', 'Bradley Hand', 'Brush Script MT', 'Chalkboard', 'Chalkboard SE', 'Chalkduster', 'Charter',
  'Cochin', 'Comic Sans MS', 'Copperplate', 'Courier', 'Courier New', 'Didot', 'DIN Alternate', 'DIN Condensed',
  'Futura',
  'Geneva', 'Georgia', 'Gill Sans', 'Helvetica', 'Helvetica Neue', 'Herculanum', 'Hoefler Text', 'Impact',
  'Lucida Grande',
  'Luminari', 'Marker Felt', 'Menlo', 'Microsoft Sans Serif', 'Monaco', 'Noteworthy', 'Optima', 'Palatino', 'Papyrus',
  'Phosphate', 'Rockwell', 'Savoye LET', 'SignPainter', 'Skia', 'Snell Roundhand', 'Tahoma', 'Times', 'Times New Roman',
  'Trattatello', 'Trebuchet MS', 'Verdana', 'Zapfino',
].sort((a, b) => a.localeCompare(b)));

interface StyleEditorState {
  error: Error;
  newStyle: string;
  styleSets: any[];
  maxFeatures: number;
}

class MaxFeaturesQueryProvider extends QueryProvider {
  maxFeatures: number;

  constructor(maxFeatures: number) {
    super();
    this.maxFeatures = maxFeatures;
  }

  getQueryLevelScales() {
    return [];
  };

  getQueryForLevel(level: number): WFSQueryOptions {
    if (this.maxFeatures) {
      return {maxFeatures: this.maxFeatures};
    } else {
      return {};
    }
  };
}

class StyleEditorComponent extends React.Component<StyleEditorProps, StyleEditorState> {
  private editor: any;
  private featureTypes;
  private serviceUrl = "";
  private wfsCapsDoc;
  private featureNames = [];

  constructor(props: StyleEditorProps) {
    super(props);

    this.state = {
      error: null,
      newStyle: null,
      maxFeatures: null,
      styleSets: []
    };
    this.createEditor = this.createEditor.bind(this);
  }

  createEditor(props: StyleEditorProps, initMessage: StyleEditorInitMessage) {
    const urlParams = new URLSearchParams(window.location.hash.replace("#/style-editor?", "?"));
    const newStyle = urlParams.get('newStyle');
    const parsedMaxFeatures = parseInt(urlParams.get('maxFeatures'), 10);
    const maxFeatures = isNaN(parsedMaxFeatures) ? null : parsedMaxFeatures;

    this.setState({newStyle, maxFeatures});
    this.serviceUrl = initMessage.url;

    return WFSCapabilities.fromURL(this.serviceUrl)
        .then((caps) => {
          for (const featureType of caps.featureTypes) {
            this.featureNames.push(featureType.name);
          }

          return Promise.all([caps, props.api.getFeatureTypesProperties(caps)]);
        })
        .catch(err => {
          if (err.message.includes("No WFS version could be negotiated.") || err.message.includes("Could not find a")) {
            this.setState({
              error: new Error(err.message)
            });
          } else {
            this.setState({
              error: new Error(
                  "{\"id\":\"studio.style-editor.wfs-invalid-document\", \"defaultMessage\":\"Invalid WFS document.\"}")
            });
          }
          return null;
        })
        .then(([caps, featureTypesProperties]) => {
          if (caps instanceof WFSCapabilities) {
            this.featureTypes = caps.featureTypes; //all feature Types, enable this line and comment out the filter below to test other CRS
          }

          if (this.featureTypes.length < 1) {
            this.setState({
              error: new Error(this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.NO_FEATURES))
            });
            return null;
          }

          const featureType = this.featureTypes[0];
          const typeName = featureType.name;

          this.wfsCapsDoc = caps;
          return {typeName, bounds: featureType.getWGS84Bounds(), featureTypesProperties};
        })
        .catch(() => {
          return null;
        })
        .then(({typeName, bounds, featureTypesProperties}) => {
          return new MaeStyleEditor({
                title: "StyleEditor",
                editor: {
                  menu: {
                    view: false,
                    background: false,
                  }
                },
                mapSource: {
                  getMap: (container, _options?) => {
                    const shouldAddGridLayer = false;
                    const restrictNavigationBounds = null;
                    const wmsReference = null;
                    const background = props.api.getPreviewBackground();
                    const map = riaApi.createMap(container, shouldAddGridLayer, restrictNavigationBounds, wmsReference,
                        background);
                    const defaultReference = this.featureTypes[0].defaultReference;
                    let reference;

                    if (defaultReference.startsWith("EPSG:") || defaultReference.startsWith("urn:ogc:def:crs:EPSG::")) {
                      const code = defaultReference.startsWith("EPSG:") ? defaultReference.substring(5)
                                                                        : defaultReference.substring(22);
                      try {
                        reference = getReference(`EPSG:${code}`);
                      } catch (e) {
                        reference = null;
                      }
                      if (!reference) {
                        let wkt = allEpsg[code].wkt;
                        if (wkt) {
                          //RIA WKTReferenceParser appears to use "of_origin" instead of "of_center"
                          wkt = wkt.replace(/longitude_of_center/ig, "longitude_of_origin");
                          wkt = wkt.replace(/latitude_of_center/ig, "latitude_of_origin");

                          //need to ignore case when comparing projection Name in node_modules/@luciad/ria/reference/format/wkt/WKTReferenceParser.js
                          //Projection Names defined in node_modules/@luciad/ria/reference/format/wkt/WKT.js
                          wkt = wkt.replace(/Albers_conic_equal_area/i, "Albers_conic_equal_area");
                          reference = parseWellKnownText(wkt);
                        } else {
                          this.setState({
                            error: new Error(this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.NO_PREVIEW))
                          });
                          wkt = allEpsg[4326].wkt;
                          reference = parseWellKnownText(wkt);
                        }
                      }
                    } else if (this.featureTypes[0].otherReferences.includes("EPSG:4326")) {
                      const wkt = allEpsg[4326].wkt;
                      reference = parseWellKnownText(wkt);
                    } else {//PROJCS/WKT
                      reference = parseWellKnownText(defaultReference);
                    }
                    if (reference?.identifier) {
                      // Add new reference to the ReferenceProvider if not present
                      if (!isValidReferenceIdentifier(reference.identifier)) {
                        addReference(reference);
                      }
                    }
                    const ref = getReference(reference.identifier);
                    const wfsStore = WFSFeatureStore.createFromCapabilities(this.wfsCapsDoc, typeName, {
                      reference: ref,
                      codec: new GMLCodec({
                        swapAxes: ["EPSG:4326"]
                      }),
                      outputFormat: "text/xml; subtype=gml/3.1.1"
                    });
                    const wfsModel = new FeatureModel(wfsStore);

                    for (const featureName of this.featureNames) {
                      const wfsLayer = new FeatureLayer(wfsModel, {
                        id: featureName,
                        label: featureName,
                        selectable: true,

                      });
                      map.layerTree.addChild(wfsLayer);
                    }
                    riaApi.fitOnBounds(map, bounds, false)

                    return map as WebGLMap;
                  },
                  getFeatureLayer: async (_map, layer) => {
                    const wfsStore = WFSFeatureStore.createFromCapabilities(this.wfsCapsDoc, layer.name, {
                      codec: new GMLCodec({
                        swapAxes: ["EPSG:4326"]
                      }),
                      outputFormat: "text/xml; subtype=gml/3.1.1"
                    });
                    const wfsModel = new FeatureModel(wfsStore);

                    return new FeatureLayer(wfsModel, {
                      id: layer.name,
                      label: layer.name,
                      selectable: true,
                      loadingStrategy: new LoadSpatially(
                          {queryProvider: new MaxFeaturesQueryProvider(this.state.maxFeatures)}),
                    });
                  },
                },
                dataSource: {
                  getSymbols: async () => {
                    return symbolList;
                  },
                  addSymbol: async (file) => {
                    const symbolHref = `./assets/${file.name}`;
                    if (!symbolList[symbolHref]) {
                      symbolList[symbolHref] = file.name;
                    }
                  },
                  deleteSymbol: async (symbolHref) => {
                    delete symbolList[symbolHref];
                  },
                  getFonts: async () => {
                    await document.fonts.ready;
                    const fontAvailable = new Set();
                    for (const font of fontCheck.values()) {
                      if (document.fonts.check(`12px "${font}"`)) {
                        fontAvailable.add(font);
                      }
                    }
                    return [...fontAvailable.values()];
                  },
                  getVectorLayers: async () => {
                    const layers = [];
                    for (const featureName of this.featureNames) {
                      const feature = this.wfsCapsDoc.featureTypes.filter(f => f.name === featureName)[0];

                      //using 4326 and WGS84 Bounds for all Features
                      const epsg = "4326";
                      const wgs84Bounds = feature.getWGS84Bounds();
                      const boundsOption = {
                        yMin: wgs84Bounds[0].y,
                        yMax: wgs84Bounds[0].width + wgs84Bounds[0].y,
                        xMin: wgs84Bounds[0].x,
                        xMax: wgs84Bounds[0].height + wgs84Bounds[0].x
                      };

                      layers.push({
                        id: featureName,
                        name: featureName,
                        crs: {
                          authorityCode: +epsg,
                          authorityName: "EPSG",
                          wkt: allEpsg[epsg].wkt,
                        },
                        bounds: boundsOption,
                        /**
                         * crs & bounds:
                         * As long mapSource.getFeatureLayer is not defined,
                         * MAE-StyleEditor uses its own internal-feature-data, so styling is still possible.
                         * If mapSource.getFeatureLayer is defined, the corresponding crs & bounds are mandatory here,
                         * otherwise a hint "bounds is missing" will be displayed for this layer.
                         */
                      })
                    }
                    return layers;
                  },
                  getPropertyNames: async (layerName) => {
                    return {
                      numeric: featureTypesProperties[layerName].filter(
                          p => p.type === FeatureTypePropertyType.NUMERIC).map(p => p.name),
                      string: featureTypesProperties[layerName].map(p => p.name)
                    };
                  },
                  getStyleSet: async () => {
                    return null;
                  },

                  saveStyleSet: async (styleSet) => {
                    const styleSets = [];
                    for (const featureName of this.featureNames) {
                      styleSets.push(styleSet[featureName]);
                    }
                    this.setState({styleSets});
                    return true;
                  },
                },
                debug: true //<- activates XML View option, only if (editor.menu.view || editor.menu) option is set to true
              } as any
          );
        })
        .catch(() => {
          return null;
        });
  }

  componentDidMount() {
    removeMainStyles();
    loadCssFile("mae-style-editor-static/index.css");
    const startupEl = document.getElementsByClassName("startup");
    if (startupEl && startupEl.length > 0) {
      const startup = startupEl[0] as HTMLElement;
      startup.style.width = "100%";
      startup.style.height = "100%";
    }

    window.addEventListener("message", (e) => {
      if (e.origin !== window.parent.origin) {
        return;
      }
      const message = e.data as StyleEditorMessage;

      if (this.isInitMessage(message)) {
        this.createEditor(this.props, message)
            .then((editor) => {
              this.editor = editor;
              this.editor.ready.then(([_editor, _i18n]) => {
                const languageAndlocale = getLanguageAndLocale();
                _i18n.addResourceBundle(
                    languageAndlocale.locale,
                    'translation',
                    {
                      Apps: {
                        StyleEditor: {
                          Button: {
                            Add_symbolizer: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.ADD_SYMBOLIZER),
                            Add_rule: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.ADD_RULE),
                            Bind_DB: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.BIND_DB),
                            Delete_rule: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.DELETE_RULE),
                            Delete_symbolizer: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.DELETE_SYMBOLIZER),
                            Delete_style: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.DELETE_STYLE),
                            Pan_To_Layer: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.PAN_TO_LAYER),
                            Hide_Layer: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.HIDE_LAYER),
                            Make_Layer_Permanent: this.props.intl.formatMessage(
                                STYLE_EDITOR_MESSAGES.MAKE_LAYER_PERMANENT),
                            No_Bounds: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.NO_BOUNDS),
                            Point_placement: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.POINT_PLACEMENT),
                            Expert_point_placement: this.props.intl.formatMessage(
                                STYLE_EDITOR_MESSAGES.EXPERT_POINT_PLACEMENT),
                            Line_placement: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.LINE_PLACEMENT),
                            Save: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.SAVE),
                          },
                          Hints: {
                            NoStyleSelected: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.NO_STYLE_SELECTED),
                            RefreshNecessary: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.REFRESH_NECESSARY),
                            InvalidExternalReference: this.props.intl.formatMessage(
                                STYLE_EDITOR_MESSAGES.INVALID_EXTERNAL_REFERENCE),
                            ContainsInvalidExternalReference: this.props.intl.formatMessage(
                                STYLE_EDITOR_MESSAGES.CONTAINS_INVALID_EXTERNAL_REFERENCE),
                            ChangesNotSaved: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.CHANGES_NOT_SAVED),
                            ScaleTooBig: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.SCALE_TOO_BIG),
                            ScaleTooSmall: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.SCALE_TOO_SMALL)
                          },
                          Wizards: {
                            ColorScheme: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.COLOR_SCHEME),
                            Categories: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.CATEGORIES),
                            Distribution: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.DISTRIBUTION),
                            Classes: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.CLASSES),
                            Field: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.FIELD),
                            Style: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.STYLE),
                            Title: {
                              Categorize: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.CATEGORIZE),
                              Gratuated: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.GRATUATED)
                            },
                            Hints: {
                              MostRelevantHint: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.MOST_RELEVANT_HINT),
                              LessThanThreeHint: this.props.intl.formatMessage(
                                  STYLE_EDITOR_MESSAGES.LESS_THAN_THREE_HINT),
                              NoDBFieldsAvailable: this.props.intl.formatMessage(
                                  STYLE_EDITOR_MESSAGES.NO_DB_FIELDS_AVAILABLE),
                              ClassifyOnlyForNumericValues: this.props.intl.formatMessage(
                                  STYLE_EDITOR_MESSAGES.CLASSIFY_ONLY_FOR_NUMERIC_VALUES),
                              MoreThan50Categories: this.props.intl.formatMessage(
                                  STYLE_EDITOR_MESSAGES.MORE_THAN_50_CATEGORIES),
                            },
                            Errors: {
                              DbFieldsCouldNotBeFetched: this.props.intl.formatMessage(
                                  STYLE_EDITOR_MESSAGES.DB_FIELDS_COULD_NOT_BE_FETCHED),
                              ValuesofDbfieldsCouldnotBeFetched: this.props.intl.formatMessage(
                                  STYLE_EDITOR_MESSAGES.VALUES_OF_DB_FIELDS_COULD_NOT_BE_FETCHED),
                            }
                          },
                          FileUploadComponent: {
                            NoFileSelected: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.NO_FILE_SELECTED),
                            InvalidReferencePleaseCheck: this.props.intl.formatMessage(
                                STYLE_EDITOR_MESSAGES.INVALID_REFERENCE_PLEASE_CHECK),
                            NoFilesAvailable: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.NO_FILES_AVAILABLE),
                          },
                          General: {
                            Text: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.TEXT),
                            Simple: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.SIMPLE),
                            Expert: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.EXPERT),
                            Simple_fill: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.SIMPLE_FILL),
                            Graphic_fill_filled_mark: this.props.intl.formatMessage(
                                STYLE_EDITOR_MESSAGES.GRAPHIC_FILL_FILLED_MARK),
                            Graphic_fill_stroked_mark: this.props.intl.formatMessage(
                                STYLE_EDITOR_MESSAGES.GRAPHIC_FILL_STROKED_MARK),
                            Graphic_fill_stroked_and_filled_mark: this.props.intl.formatMessage(
                                STYLE_EDITOR_MESSAGES.GRAPHIC_FILL_STROKED_AND_FILLED_MARK),
                            Graphic_fill_external_graphic: this.props.intl.formatMessage(
                                STYLE_EDITOR_MESSAGES.GRAPHIC_FILL_EXTERNAL_GRAPHIC),
                            Custom_fill: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.CUSTOM_FILL),
                            Stroked_mark: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.STROKED_MARK),
                            Filled_mark: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.FILLED_MARK),
                            Filled_and_stroked_mark: this.props.intl.formatMessage(
                                STYLE_EDITOR_MESSAGES.FILLED_AND_STROKED_MARK),
                            External_graphic: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.EXTERNAL_GRAPHIC),
                            Custom_halo: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.CUSTOM_HALO),
                            Custom_graphic: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.CUSTOM_GRAPHIC),
                            Simple_line: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.SIMPLE_LINE),
                            Dashed_line: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.DASHED_LINE),
                            Graphic_stroke_Stroked_mark: this.props.intl.formatMessage(
                                STYLE_EDITOR_MESSAGES.GRAPHIC_STROKE_STROKED_MARK),
                            Graphic_stroke_Filled_mark: this.props.intl.formatMessage(
                                STYLE_EDITOR_MESSAGES.GRAPHIC_STROKE_FILLED_MARK),
                            Graphic_stroke_FilledAndStroked_mark: this.props.intl.formatMessage(
                                STYLE_EDITOR_MESSAGES.GRAPHIC_STROKE_FILLED_AND_STROKED_MARK),
                            Graphic_stroke_External_graphic: this.props.intl.formatMessage(
                                STYLE_EDITOR_MESSAGES.GRAPHIC_STROKE_EXTERNAL_GRAPHIC),
                            Left: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.LEFT),
                            Bottom: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.BOTTOM),
                            Right: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.RIGHT),
                            Top: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.TOP),
                            Center: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.CENTER),
                            Square: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.SQUARE),
                            Circle: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.CIRCLE),
                            Triangle: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.TRIANGLE),
                            Cross: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.CROSS),
                            Star: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.STAR),
                            Unit: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.UNIT),
                            Fill: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.FILL),
                            Fill_color: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.FILL_COLOR),
                            Fill_opacity: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.FILL_OPACITY),
                            Stroke: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.STROKE),
                            Stroke_width: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.STROKE_WIDTH),
                            Stroke_color: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.STROKE_COLOR),
                            Stroke_opacity: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.STROKE_OPACITY),
                            Name: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.NAME),
                            Label_text: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.LABEL_TEXT),
                            Horizontal: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.HORIZONTAL),
                            Vertical: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.VERTICAL),
                            Label_placement: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.LABEL_PLACEMENT),
                            Graphic_size: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.GRAPHIC_SIZE),
                            Graphic_fill: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.GRAPHIC_FILL),
                            Graphic_image_size: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.GRAPHIC_IMAGE_SIZE),
                            Font_family: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.FONT_FAMILY),
                            Font_size: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.FONT_SIZE),
                            Font_weight: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.FONT_WEIGHT),
                            Font_style: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.FONT_STYLE),
                            Opacity: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.OPACITY),
                            Rotation: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.ROTATION),
                            Displacement_X: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.DISPLACEMENT_X),
                            Displacement_Y: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.DISPLACEMENT_Y),
                            Marktype: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.MARKTYPE),
                            Dasharray: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.DASHARRAY),
                            Dashoffset: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.DASHOFFSET),
                            Initial_gap: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.INITIAL_GAP),
                            Gap: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.GAP),
                            Halo: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.HALO),
                            Radius: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.RADIUS),
                            AnchorPoint: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.ANCHOR_POINT),
                            Is_aligned: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.IS_ALIGNED),
                            Is_repeated: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.IS_REPEATED),
                            Perpendicular_offset: this.props.intl.formatMessage(
                                STYLE_EDITOR_MESSAGES.PERPENDICULAR_OFFSET),
                            Graphic_references: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.GRAPHIC_REFERENCES),
                            None: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.NONE),
                            Custom: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.CUSTOM),
                            Linecap: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.LINECAP),
                            Graphic: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.GRAPHIC),
                            File: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.FILE),
                            Linejoin: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.LINEJOIN),
                            Database: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.DATABASE),
                            Select_a_file: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.SELECT_A_FILE),
                            Upload_a_file: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.UPLOAD_A_FILE),
                            FileUploadError: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.FILE_UPLOAD_ERROR),
                            FileDeleteError: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.FILE_DELETE_ERROR),
                            Select_field: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.SELECT_FIELD),
                            Delete_custom_Rule: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.DELETE_CUSTOM_RULE),
                            Unlock_Rule: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.UNLOCK_RULE),
                            ShowBigPreview: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.SHOW_BIG_PREVIEW),
                            HideBigPreview: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.HIDE_BIG_PREVIEW),
                            Unlock_SymbologyType: this.props.intl.formatMessage(
                                STYLE_EDITOR_MESSAGES.UNLOCK_SYMBOLOGY_TYPE),
                            Lock_SymbologyType: this.props.intl.formatMessage(
                                STYLE_EDITOR_MESSAGES.LOCK_SYMBOLOGY_TYPE),
                            SymbologyType: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.SYMBOLOGY_TYPE),
                            Symbolizer: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.SYMBOLIZER),
                            SymbolLoadError: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.SYMBOL_LOAD_ERROR),
                            From: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.FROM),
                            To: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.TO),
                            Scale: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.SCALE),
                            Search: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.SEARCH),
                            Filter: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.FILTER),
                            Rule: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.RULE),
                            Loading: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.LOADING),
                            Unstyled_Layer: this.props.intl.formatMessage(STYLE_EDITOR_MESSAGES.UNSTYLED_LAYER),
                            Initializing_StyleEditor: this.props.intl.formatMessage(
                                STYLE_EDITOR_MESSAGES.INITIALIZING_STYLEEDITOR),
                            Something_Went_Wrong: this.props.intl.formatMessage(
                                STYLE_EDITOR_MESSAGES.SOMETHING_WENT_WRONG)
                          }
                        }
                      }
                    } as IStyleEditorLocalization,
                    true
                );
                this.editor.render();
              });
            });
      }

      if (this.isSaveMessage(message)) {
        this.save();
      }
    });

    this.postMessage({
      command: StyleEditorCommand.MOUNTED,
    });
  }

  private postMessage(message: StyleEditorMessage) {
    window.top.postMessage(message, window.parent.origin);
  }

  private isInitMessage(data: StyleEditorMessage): data is StyleEditorInitMessage {
    return data.command === StyleEditorCommand.INIT;
  }

  private isSaveMessage(data: StyleEditorMessage): data is StyleEditorMessage {
    return data.command === StyleEditorCommand.SAVE;
  }

  renderError() {
    const {error} = this.state;

    if (error) {
      try {
        const message = JSON.parse(error.message);
        if (message.id && message.defaultMessage) {
          return <Alert bsStyle="danger">
            {/* tslint:disable-next-line:lcd-no-variables-in-translation-messages */}
            <FormattedMessage id={message.id} defaultMessage={message.defaultMessage}/>
          </Alert>;
        }
      } catch (e) {
        return <Alert bsStyle="danger">{error.message}</Alert>;
      }
    }
    return null;
  }

  render() {
    return <React.Fragment>
      {/* tslint:disable */}
      {/* Disabled because: String literals are disallowed as JSX. Use a translation function */}
      <style>{`
      html,
      body,
      #root {
          width: 100%;
          height: 100%;
          overflow-x: hidden;
      }
    `}</style>
      {/* tslint:enable */}
      {this.renderError()}
      <style-editor></style-editor>
    </React.Fragment>;
  }

  private save() {
    //generates style and calls dataSource.saveStyle -> returns boolean
    this.editor.save()
        .then(() => {
          const {styleSets} = this.state;
          const layers = [];
          const newStyle = this.state.newStyle !== null ? this.state.newStyle : "NewStyle";

          for (let i = 0; i < styleSets.length; i++) {
            if (styleSets[i]) {
              layers.push(
                  ApolloApi.saveSLDStyle(`${newStyle}_${this.featureNames[i].replace(/.*:/, "").replace(/.*__/, "")}`,
                      styleSets[i]));
            }
          }
          Promise.allSettled(layers).then(((response) => {
            //todo: Add SLD validation/error handling here
            const results = response.filter((res) => res.status === 'fulfilled') as PromiseFulfilledResult<any>[];
            if (response.length !== results.length) {
              this.setState({
                error: new Error("Unable to save new Style.")
              });
            }
          }));
        });
  };
}

export const StyleEditor = WithApi(injectIntl(StyleEditorComponent));